import React, { useState } from 'react';
import { motion as Motion } from 'framer-motion';
import { siteData } from '../data';
import { Mail, MapPin, Phone, Send, Loader2, CheckCircle, Info } from 'lucide-react';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <Motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight"
          >
            Kontakt Os
          </Motion.h1>
          <Motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-400 text-lg max-w-2xl mx-auto"
          >
            Har du spørgsmål til vores menu, eller er du interesseret i en frokostordning? Vi sidder klar til at hjælpe.
          </Motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <Motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-12"
          >
            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">Besøg os</h3>
                  <p className="text-zinc-400 leading-relaxed">{siteData.company.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">Ring til os</h3>
                  <a href={`tel:${siteData.company.phone.replace(/\s/g, '')}`} className="text-zinc-400 hover:text-white transition-colors leading-relaxed">
                    {siteData.company.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">Send en mail</h3>
                  <a href={`mailto:${siteData.company.email}`} className="text-zinc-400 hover:text-white transition-colors leading-relaxed">
                    {siteData.company.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="">
                  <Info className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">CVR</h3>
                  <p className="text-zinc-400 leading-relaxed">{siteData.company.cvr}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 p-8 rounded-3xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4 uppercase tracking-wide">Åbningstider</h3>
              <div className="space-y-3">
                {siteData.company.openingHours.map((item) => (
                  <div key={item.day} className="flex justify-between text-zinc-400">
                    <span>{item.day}:</span>
                    <span className="font-semibold">{item.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </Motion.div>

          {/* Contact Form */}
          <Motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-red-600 p-8 md:p-12 rounded-3xl shadow-xl border border-white/10"
          >
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="bg-green-900/30 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-wide">Tak for din besked!</h3>
                <p className="text-white/90">Vi vender tilbage til dig så hurtigt som muligt.</p>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="mt-8 text-white font-bold hover:underline"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-semibold text-white">Navn</label>
                    <input
                      required
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/40 text-white focus:ring-2 focus:ring-white focus:border-transparent outline-none transition-all placeholder:text-white/60"
                      placeholder="Dit fulde navn"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-semibold text-white">Email</label>
                    <input
                      required
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/40 text-white focus:ring-2 focus:ring-white focus:border-transparent outline-none transition-all placeholder:text-white/60"
                      placeholder="din@email.dk"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="subject" className="text-sm font-semibold text-white">Emne</label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/40 text-white focus:ring-2 focus:ring-white focus:border-transparent outline-none transition-all [&>option]:text-zinc-900"
                  >
                    <option value="frokostordning">Frokostordning</option>
                    <option value="generelt">Generelt spørgsmål</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-semibold text-white">Besked</label>
                  <textarea
                    required
                    id="message"
                    name="message"
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/40 text-white focus:ring-2 focus:ring-white focus:border-transparent outline-none transition-all resize-none placeholder:text-white/60"
                    placeholder="Hvad kan vi hjælpe dig med?"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Sender...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" /> Send besked
                    </>
                  )}
                </button>
              </form>
            )}
          </Motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
